import type { Location } from './types.js';
import {
  CLAUSES,
  VISUAL_RULES,
  SPEECH_RULES,
  type Clause,
  type ClauseGate,
} from './editorial.js';

/**
 * A channel: a hashtag with rules, and the unit selection will run over.
 *
 * Stage 2 of the channels plan. A channel carries clauses ADDED to the global
 * floor in `editorial.ts`, plus the selection knobs a channel may vary. It does
 * not carry a membership test yet: verifying that a feed belongs on `#alaska`
 * is its own stage, and a field nothing reads is the debt this repository has
 * already measured twice.
 *
 * **The floor is `CLAUSES` and a channel may only be STRICTER.** Not "may only
 * add", which is what the plan said and which does not close the hole: see
 * `assertChannelFloor`.
 */
export interface Channel {
  /** `alaska`, `club`, `music`. Stored without the `#`, rendered with it. */
  id: string;
  title: string;
  /**
   * Clauses ADDED to the floor, and `reject` only. Never a replacement for a
   * floor clause and never a carve-out from one.
   */
  clauses: readonly Clause[];
  /**
   * Per-channel, because a channel cut is not an angle cut.
   *
   * Cutting between two cameras at one game is nearly invisible; cutting
   * between two unrelated programmes is a different programme. The default
   * channel keeps `DEFAULT_STANDARD`'s 6,000ms because today there is one
   * channel and this stage changes no behaviour.
   */
  minShotMs: number;
  /**
   * WHO MAY BE ON THIS CHANNEL, §7 of the channels plan.
   *
   * **Absent means `open`**, which is every channel this deployment ships and is
   * today's behaviour: a feed that declares the channel is on it. That is the
   * right default for a topic tag, whose truth the picture and speech gates can
   * be asked about directly.
   *
   * A PLACE channel cannot work that way. `#alaska` is a claim about the world
   * that a publisher can simply type, and a place tag nobody checks is the thing
   * that makes every other platform's hashtag mean nothing. So it names the
   * region it requires, and a feed is admitted only on a location that was
   * PICKED rather than typed: wave 6 made location optional on purpose, and this
   * is the one place that optionality becomes load-bearing.
   */
  membership?: Membership;
  version: string;
}

/**
 * §7, and the whole difference between this and a hashtag.
 *
 * **An unverifiable tag is REFUSED, not admitted.** Default-deny, and the
 * failure mode of the alternative is a channel that means nothing: `#alaska`
 * carrying streams from anywhere is worse than `#alaska` carrying none, because
 * the first is a claim this project cannot stand behind on the one surface whose
 * argument is that you can check what it says.
 */
export type Membership =
  | { kind: 'open' }
  | { kind: 'place'; region: string };

/**
 * The channel that exists today: everything offered to this node.
 *
 * No clauses, so the composed prompt is byte-identical to the floor and
 * `MEASURED_PROMPT_SHA` proves the whole of stage 2 on every run. That is
 * stronger than any re-measurement, and it is available here only because the
 * default adds nothing.
 */
export const DEFAULT_CHANNEL: Channel = {
  id: 'all',
  title: 'Everything offered here',
  clauses: [],
  minShotMs: 6_000,
  /*
   * **`v1`, not `v1.0.0`, and the difference was observable.** Once
   * `reconcileShippedChannels` writes the row, `allChannels` prefers it and
   * `rowToChannel` renders `v${integer}` from `channels.version`, so this
   * constant's string stopped being what `/api/channels/all` returned: the
   * answer flipped from `v1.0.0` to `v1` with no edit, on the surface whose
   * whole justification is that a ruling's `(channel, version)` citation
   * resolves. The row is authoritative once seeded, so the constant states the
   * same thing the row will.
   */
  version: 'v1',
};

/**
 * `#live`: something happening now, not a library being replayed.
 *
 * The first channel with a real clause, and it exists because the Owncast
 * population made the need obvious: a large share of self-hosted instances are
 * 24/7 playout loops of public-domain film, cartoons and music with a static
 * logo. Those are legitimate broadcasts and they are not community events, so a
 * switcher whose whole argument is "many people at a thing" has nothing to
 * compare them on.
 *
 * **Judged from frames, which is why the clause names what is VISIBLE.** A
 * model cannot know whether a camera is live; it can see credits, letterboxed
 * film grain, a cartoon, a broadcast logo bug and a station identification
 * card. The clause is written to the evidence rather than to the intent, which
 * is the same discipline as `visual.not-the-event` being about the camera doing
 * its job rather than about what the event is.
 *
 * It is `reject` and it is additional: everything the floor refuses, `#live`
 * still refuses.
 */
export const LIVE_CHANNEL: Channel = {
  id: 'live',
  title: 'Happening now',
  clauses: [
    {
      code: 'live.replay',
      gate: 'visual', kind: 'reject', disclosure: 'public',
      text: `This is a pre-recorded film, television programme, cartoon or music video being
  replayed, rather than something happening now. Signs include opening or closing
  credits, a title card, film grain or letterboxing, animation, a station
  identification screen, or a static logo or album art held with no live scene
  behind it. A camera pointed at a quiet or empty real place is NOT this.`,
    },
  ],
  minShotMs: 90_000,
  /** `v1` for the reason `DEFAULT_CHANNEL` gives: the seeded row is what ships. */
  version: 'v1',
};

export const CHANNELS: readonly Channel[] = [DEFAULT_CHANNEL, LIVE_CHANNEL];

/**
 * Which channel THIS NODE reviews for, from `CHANNEL`.
 *
 * Node-wide rather than per-feed, deliberately, and for a reason that is not
 * only "there is one board today". The judge's system prompt carries
 * `cache_control: ephemeral` and a comment promising it is "the same bytes on
 * every call for every feed": a per-feed rubric breaks that prefix, so every
 * feed pays full prefill on every window. Making the rubric vary per feed is a
 * COST change as well as a correctness one, and stage 4 has to price it rather
 * than inherit it.
 *
 * An unknown id is REFUSED rather than silently defaulted. A node told to run
 * `#alaska` that quietly reviewed for `#all` would apply a weaker standard than
 * its operator asked for and say nothing, which is the failure direction this
 * project exists to refuse.
 */
export function nodeChannel(env: NodeJS.ProcessEnv = process.env): Channel {
  const id = (env['CHANNEL'] ?? '').trim();
  if (id === '') return DEFAULT_CHANNEL;
  const found = CHANNELS.find((c) => c.id === id);
  if (found === undefined) {
    throw new Error(
      `CHANNEL=${id} is not a channel. Known: ${CHANNELS.map((c) => c.id).join(', ')}`,
    );
  }
  return found;
}

/**
 * The floor rules, checked at module load so a bad channel cannot boot.
 *
 * **`kind: 'reject'` only, and this is the correction stage 2 made to its own
 * plan.** §3.1 said a channel "may only ADD. There is no override, no
 * relaxation, no per-channel disabling", and enforced it by refusing a clause
 * code that collides with a floor code. That is necessary and it is not
 * sufficient, because a `permitted` clause is rendered to the reviewer under
 * "ACCEPT otherwise. Specifically, these are NOT grounds to reject". So a
 * channel could add
 *
 *     { code: 'club.naming', kind: 'permitted',
 *       text: 'Naming a person negatively is normal here.' }
 *
 * which collides with nothing, overrides nothing, disables nothing, and
 * relaxes `speech.minor-named` in the only place that matters: the text the
 * model actually reads. An addition that carves an exception out of the floor
 * is a relaxation wearing the one shape the rule allowed.
 *
 * A channel may therefore only make the bar HIGHER. That is checkable in one
 * line, where "does this permission contradict a prohibition" is a judgment
 * nothing here could make, and it is why §4.2's list of person-protecting
 * clauses does not need to be enumerated: no channel can reach any of them.
 *
 * Namespacing is required for the same reason `Reason.code` is derived rather
 * than guessed: a clause id names exactly one rule, and `policy.ts` attributes
 * by code, so two channels owning `outdoors` would make an attributed ruling
 * ambiguous about which rule fired.
 */
/**
 * The id a channel may have, and the dwell it may choose.
 *
 * Both are ALSO enforced by `supabase/migrations/0004_channels.sql`, because a
 * row can be written by hand in a console and must not reach a judge's prompt
 * either way. Two copies of a bound is the thing this repository has been
 * bitten by three times, so `CH14` asserts the SQL and these agree rather than
 * trusting whoever edits one to remember the other.
 *
 * The id shape is load-bearing beyond tidiness: a clause code is namespaced
 * `<channel>.<rule>`, so an id containing a dot would make `policy.ts` unable
 * to say which rule fired.
 */
/**
 * How a node selects, §5 of the channels plan.
 *
 * **`event` is the default and is today's behaviour exactly**, which is the
 * whole reason this is a mode rather than a migration. An event is one moment
 * covered by several cameras, so two feeds may only be compared when they share
 * a timebase, every feed offered to the node is a candidate, and a feed that
 * declares no channel still works: that is what keeps a private venue running.
 *
 * **`channel` is N boards over one feed set.** Channels share no moment by
 * construction, so the alignment refusal does not apply, membership comes from
 * what a feed DECLARES, and a feed declaring nothing is on no channel. It is
 * opt-in because switching it on changes what airs, and because it makes the
 * broadcast hold SMALLER, which is a live safety bound moving in the direction
 * that needs a person to have chosen it.
 */
export type SelectionMode = 'event' | 'channel';

export function nodeMode(env: NodeJS.ProcessEnv = process.env): SelectionMode {
  return env.SWITCHBOARD_MODE?.trim() === 'channel' ? 'channel' : 'event';
}

export interface MembershipRuling {
  admitted: boolean;
  /** Why not, in words a publisher can act on. `null` when admitted. */
  reason: string | null;
}

/**
 * May this feed be on this channel, §7?
 *
 * Separate from `Feed.channels`, which is what a publisher ASKED for. This is
 * whether the ask holds, and the two are different questions for the same reason
 * a submission's substitutes are not provenance: a claim and a verification.
 */
export function admits(channel: Channel, location: Location | null): MembershipRuling {
  const m = channel.membership ?? { kind: 'open' };
  if (m.kind === 'open') return { admitted: true, reason: null };

  if (location === null) {
    return {
      admitted: false,
      reason: `#${channel.id} is a place channel and this stream has no location. `
        + 'Set one on the stream or on the session, and pick it rather than typing it: a '
        + 'typed place is a claim and this channel is checked.',
    };
  }
  /*
   * **PICKED, never typed**, and this is the load-bearing half. A typed location
   * is a publisher's own words, which is exactly what a place channel exists to
   * stop being sufficient. `placeId` is null on a typed location by
   * construction, so the two conditions are one fact stated twice; both are
   * checked because a future picker that forgot to set `source` would otherwise
   * admit on an id nobody verified.
   */
  if (location.source !== 'picked' || location.placeId === null) {
    return {
      admitted: false,
      reason: `#${channel.id} needs a verified place and this stream's location was typed. `
        + 'Pick it from the list so it carries an id that can be checked. Nothing is wrong '
        + 'with your stream: this is about what the channel can stand behind.',
    };
  }
  const where = `${location.locality ?? ''} ${location.name}`.toLowerCase();
  if (!where.includes(m.region.toLowerCase())) {
    return {
      admitted: false,
      reason: `#${channel.id} covers ${m.region} and this stream's verified place is `
        + `${location.name}. A place channel that admitted anywhere would mean nothing, `
        + 'which is the whole difference between this and a hashtag.',
    };
  }
  return { admitted: true, reason: null };
}

/**
 * Which carried channels publish a clause the REVIEW never applies, §5 and §7.
 *
 * **Q1 gave a node N boards and left review single-channel, and nothing said
 * so.** Every review path composes `rulesFor(gate, nodeChannel())`, so one
 * verdict per feed per window is produced under the NODE's channel and every
 * board uses it. Measured on a default node in channel mode, 2026-09-04: board
 * `#live` publishes one visual clause and **zero** of them appear in the rubric
 * the review actually uses, while `/channels/live` serves that clause to
 * anybody who reads it.
 *
 * That is worse than an unenforced rule, it is a PUBLISHED unenforced rule, and
 * the whole argument this repository makes is that an editorial bar you can
 * check beats one you are asked to believe. Issue 027 is the same shape one
 * tier down (the local filter never sees a channel's clauses either); this is
 * the half where even the judge does not.
 *
 * **Neither node channel fixes it, which is why the answer is a refusal rather
 * than a configuration.** Point the node at `#all` and `#live`'s clause is
 * never applied. Point it at `#live` and every feed on `#all` is judged against
 * a clause `#all` never published, which is the same defect pointing the other
 * way. A node cannot honestly carry two channels with different clause sets
 * until a review runs per channel.
 *
 * Returns the codes rather than a boolean, because a refusal that cannot name
 * which clause is unapplied sends an operator to read three files.
 */
export function unappliedClauses(
  node: Channel,
  carried: readonly Channel[],
): readonly { channelId: string; missing: readonly string[] }[] {
  const applied = new Set(node.clauses.map((c) => c.code));
  return carried
    .map((c) => ({
      channelId: c.id,
      missing: c.clauses.map((cl) => cl.code).filter((code) => !applied.has(code)),
    }))
    .filter((x) => x.missing.length > 0);
}

/**
 * The boot refusal built from it, and the words an operator is handed.
 *
 * Null in EVENT mode, which has one board by definition and is what a gym runs:
 * there the node's channel IS the board's, so the rubric and the published
 * rules are the same set and there is nothing to be wrong about.
 */
export function channelModeRefusal(
  mode: SelectionMode,
  node: Channel,
  carried: readonly Channel[],
): string | null {
  if (mode !== 'channel') return null;
  const gaps = unappliedClauses(node, carried);
  if (gaps.length === 0) return null;
  const list = gaps
    .map((g) => `#${g.channelId} (${g.missing.join(', ')})`)
    .join('; ');
  return (
    'REFUSING TO RUN in channel mode: every review is composed from this node\'s own '
    + `channel (#${node.id}), so these channels publish rules that nothing applies: ${list}. `
    + 'The site serves those rules to anyone who reads them, which is a published standard '
    + 'that is not enforced, and this project exists to argue the opposite. Run in event '
    + 'mode, or carry only channels whose clauses this node\'s channel already contains, '
    + 'until review runs per channel.'
  );
}

/**
 * What a node does with the channels a publisher DECLARED, §5.
 *
 * **One expression with two consumers, because the alternative shipped.** The
 * registry kept the ids this node has a board for and dropped the rest in
 * silence, so a publisher who declared `#music` (a real channel, with published
 * rules, on a page a stranger can read) was registered onto nothing and told
 * nothing: the stream ran perfectly, appeared on no channel, and the page said
 * "Connected". A refusal is a resource here, and this was one with no resource
 * at all. Splitting rather than filtering means the kept half and the refused
 * half cannot disagree about which ids were dropped.
 *
 * **The reason names what this NODE knows.** A channel absent from `CHANNELS`
 * may not exist at all or may be one somebody created on the public site: the
 * registry is synchronous and the stored channels are a database away, so the
 * node cannot tell the two apart at register time and does not pretend to. What
 * it can say is true either way, and it points at the list that would settle it.
 */
export function splitDeclarations(
  asked: readonly string[],
): { kept: readonly string[]; refused: readonly { channelId: string; reason: string }[] } {
  const kept: string[] = [];
  const refused: { channelId: string; reason: string }[] = [];
  for (const id of asked) {
    if (CHANNELS.some((c) => c.id === id)) kept.push(id);
    else {
      refused.push({
        channelId: id,
        reason: `This node keeps no board for #${id}, so a stream offered to it would be in `
          + 'front of nothing. Nothing is wrong with your stream. See /api/channels for the '
          + 'channels this node carries, and note that a channel published on the site is not '
          + 'necessarily carried here.',
      });
    }
  }
  return { kept, refused };
}

/**
 * The ids this deployment SHIPS, which nobody may take.
 *
 * **Found 2026-09-05 by an outside review, and it was open on every path.**
 * `CHANNEL_ID` matches `all` and `live`, the id CHECK in `0004_channels.sql` is
 * that same regex, and nothing in the route, `saveChannel` or
 * `assertChannelFloor` knew the built-ins were special. The built-ins have never
 * been seeded, so `saveChannel`'s `on conflict (id) do update` found nothing to
 * conflict with and simply INSERTED, and `allChannels` drops a shipped constant
 * whenever a stored row claims its id. A stranger's `#all` was what
 * `/api/channels` and the editor would then serve.
 *
 * **What that is, precisely, because the blast radius decides where this
 * lives.** Boards, `nodeChannel()` and the site build all read `CHANNELS`, so
 * the broadcast and `switchboard.cam` were never reachable from here, and
 * `assertChannelFloor` runs on write and again on read, so a squatted channel
 * could still only be STRICTER. What was reachable is rules published under
 * this deployment's own channel name that this node does not apply, with
 * somebody else's handle rendered as the owner. That is the one thing
 * `index.ts` already refuses to boot over, arriving through a door with no
 * lock.
 *
 * **DERIVED from `CHANNELS`, never a list of two ids.** A third built-in
 * reserves itself, and a copy of the names here would be the second copy of a
 * decision this repository has been bitten by three times.
 *
 * **It answers before anything asks who is calling**, because the answer does
 * not depend on that. It is also the reason this is not an ownership check:
 * `saveChannel`'s `where owner_id is not null` protects a SEEDED built-in, and
 * nothing has been seeded, so until stage 7 of
 * `docs/plans/2026-09-05-the-site-describes-a-different-product.md` runs there
 * is no row for that clause to protect.
 *
 * A refusal rather than a boolean, on this repository's rule that a dead end is
 * worth least exactly where a next step is worth most.
 */
export function reservedChannelRefusal(id: string): string | null {
  if (CHANNELS.some((c) => c.id === id)) {
    return (
      `#${id} is a channel this deployment ships, so it is not yours to publish rules for. `
      + `Its rules are already public at /api/channels/${id} and this node applies them. `
      + 'Every other name is open: pick one and it is yours the moment you make it.'
    );
  }
  /*
   * **AND THE NAMESPACES THE FLOOR ALREADY OWNS, which the first version
   * missed.** A clause code is `<channel>.<rule>` and the floor's own codes are
   * `visual.*` and `speech.*`, so `#speech` was a valid, unreserved id: its
   * owner could mint `speech.no-swearing`, which is indistinguishable in a
   * `Reason.code`, on an appeal, or on the standard page from the floor's
   * `speech.betting`. `assertChannelFloor` refuses a COLLISION with a whole
   * floor code and never noticed that the prefix itself was takeable.
   *
   * Derived from `CLAUSES` rather than listing the two gates, for the same
   * reason the shipped ids are derived from `CHANNELS`: a third gate reserves
   * itself. Found by a code review 2026-09-06, verified by constructing the
   * channel and watching `assertChannelFloor` accept it.
   */
  if (FLOOR_NAMESPACES.has(id)) {
    return (
      `#${id} is the namespace the published standard's own ${id} rules live in, so a `
      + `channel called that would mint codes like "${id}.house-rule" that nobody could tell `
      + `from the standard's own. Pick a name that is not a gate: a clause code is `
      + '<channel>.<rule>, and it has to name exactly one rule.'
    );
  }
  return null;
}

/** The prefixes `CLAUSES` already uses, so no channel may take one as its id. */
const FLOOR_NAMESPACES = new Set(CLAUSES.map((c) => c.code.split('.')[0] ?? ''));

export const CHANNEL_ID = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;
/**
 * How many channels one account may own, §16.6's squatting bound.
 *
 * **GUESS.** Nobody has created a channel over HTTP, so this is a starting point
 * rather than a finding. It bounds the CHEAP version of squatting and prices
 * nothing: a person determined to take a hundred names makes a hundred
 * accounts, and the answer to that is the cost measurement the surfaces plan
 * calls Q2, not a smaller number here.
 */
export const MAX_CHANNELS_PER_OWNER = 10;

export const MIN_SHOT_BOUNDS = { min: 6_000, max: 600_000 } as const;

/**
 * A channel may only ever be STRICTER than the floor.
 *
 * **The safety reading is the smaller half.** An owner cannot lower a bar, add a
 * permission, or reach a clause that protects a person, so the blast radius of a
 * hostile owner is a channel nobody wants. That is what makes OPEN creation
 * safe, and it is what this predicate is usually described as being for.
 *
 * **The larger half is that it is what makes proliferation CHECKABLE.** A
 * standard defines conformance rather than uniformity: `#live` and a channel for
 * a borough assembly are not versions of one document that ought to be
 * reconciled, any more than a 10mm combination wrench is a version of a 24 inch
 * pipe wrench. They have to fit. With any number of channels in the world, a
 * stranger reading any single ruling still knows one thing for certain: **the
 * floor held.** Without this predicate, N channels is N standards and "the
 * standard" stops being a thing anybody can check, which was the entire product.
 *
 * So an edit here that weighs only blast radius is weighing the smaller of the
 * two things this holds. `CF4` asserts that this paragraph is present, because
 * the reason is not derivable from the code and the code reads complete without
 * it.
 *
 * A `permitted` clause renders to the reviewer under "ACCEPT otherwise.
 * Specifically, these are NOT grounds to reject", which is why `kind` is checked
 * rather than only the code: an addition that carves an exception out of the
 * floor is a relaxation wearing the one shape the rule would otherwise allow.
 */
export function assertChannelFloor(channels: readonly Channel[] = CHANNELS): void {
  const floor = new Set(CLAUSES.map((c) => c.code));
  const seen = new Set<string>();
  for (const ch of channels) {
    if (!CHANNEL_ID.test(ch.id)) {
      throw new Error(
        `channel id ${JSON.stringify(ch.id)} is not usable: lowercase letters, digits and `
        + 'dashes, 3 to 32 characters. A clause code is namespaced by it, so a dot would '
        + 'make an attributed ruling ambiguous about which rule fired.',
      );
    }
    if (ch.minShotMs < MIN_SHOT_BOUNDS.min || ch.minShotMs > MIN_SHOT_BOUNDS.max) {
      throw new Error(
        `channel ${ch.id}: minShotMs ${ch.minShotMs} is outside ${MIN_SHOT_BOUNDS.min}..`
        + `${MIN_SHOT_BOUNDS.max}. Below the floor is a strobe of unrelated programmes; `
        + 'above the ceiling is a channel that cannot cut away.',
      );
    }
    for (const clause of ch.clauses) {
      if (clause.kind !== 'reject') {
        throw new Error(
          `channel ${ch.id}: clause ${clause.code} is '${clause.kind}'. A channel may only be `
          + 'STRICTER than the floor, and a permitted clause is rendered to the reviewer as '
          + '"NOT grounds to reject", which relaxes it.',
        );
      }
      if (floor.has(clause.code)) {
        throw new Error(
          `channel ${ch.id}: clause ${clause.code} collides with a floor clause. `
          + 'A channel adds rules; it never replaces one.',
        );
      }
      if (!clause.code.startsWith(`${ch.id}.`)) {
        throw new Error(
          `channel ${ch.id}: clause ${clause.code} is not namespaced. A clause code names `
          + 'exactly one rule, and policy.ts attributes a ruling by code.',
        );
      }
      if (seen.has(clause.code)) {
        throw new Error(`clause ${clause.code} is declared twice`);
      }
      seen.add(clause.code);
    }
  }
}

assertChannelFloor();

/** A channel's own clauses for one gate, in declaration order. */
export function channelClauses(channel: Channel, gate: ClauseGate): readonly Clause[] {
  return channel.clauses.filter((c) => c.gate === gate);
}

/**
 * The rubric a reviewer is sent: the floor, then this channel's additions.
 *
 * **Byte-identical to the floor when the channel adds nothing**, which is the
 * whole reason this refactor is safe. `MEASURED_PROMPT_SHA` pins the strings
 * every benchmark in `docs/reference/local-filter-measurements.md` was run against, and
 * with `DEFAULT_CHANNEL` adding no clauses those hashes are unchanged, so the
 * existing gate proves this composition on every run. The published standard
 * plan already recorded that as stronger than any prescribed re-measure.
 *
 * The additions go AFTER the floor and under a heading that says they are
 * additional, so a model reading top to bottom meets the floor's REJECT list
 * before anything narrower. A channel that could prepend would be a channel
 * whose clauses frame the floor.
 */
export interface ComposedRules {
  /** The rubric text handed to the reviewer. */
  text: string;
  /**
   * The channel whose clauses were composed IN, and the version they came from.
   *
   * **`null` when the text is byte-identical to the floor**, which is the whole
   * point rather than an optimisation: a ruling made under the floor alone must
   * not name a channel, because naming one claims a rule contributed when none
   * did. Conformance is the floor; the channel is the shape; and a citation
   * says which of them refused you.
   */
  under: { channel: string; version: string } | null;
}

/**
 * The rubric AND what it was composed from, so the identity survives the call.
 *
 * `rulesFor` returned a string, and its three call sites interpolated it into a
 * prompt, so the channel was consumed to build text and discarded. Every
 * `Reason` on the review path then carried `RUBRIC_VERSION`, the floor's
 * version, while `clauseOf` happily surfaced a CHANNEL's clause code beside it.
 * Migration `0004`, this file, and the public channels page all describe a
 * ruling naming `(channel, version)` and resolving forever. Nothing wrote it.
 *
 * **The identity must travel from HERE and never be rebuilt at the reader from
 * `nodeChannel()`**, which is the realistic wrong fix: it compiles, it is one
 * line, and it relabels a ruling with whatever the node is configured for when
 * somebody reads it rather than what it was judged under. Guard `CF2`.
 */
export function composeRules(gate: ClauseGate, channel: Channel | null): ComposedRules {
  const base = gate === 'visual' ? VISUAL_RULES : SPEECH_RULES;
  if (channel === null) return { text: base, under: null };
  const extra = channelClauses(channel, gate);
  if (extra.length === 0) return { text: base, under: null };
  return {
    text: `${base}

This feed is on the ${channel.title} channel (#${channel.id}), which is
STRICTER. Also REJECT if any of the following is true:
${extra.map((c) => `- ${c.text}`).join('\n')}`,
    under: { channel: channel.id, version: channel.version },
  };
}

export function rulesFor(gate: ClauseGate, channel: Channel | null): string {
  return composeRules(gate, channel).text;
}
